/** Phase 3B-2a — Confidence Calibration section. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, ScatterChart, Scatter } from "recharts";
import { AlertTriangle, Info, Download } from "lucide-react";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { downloadCsv } from "@/lib/csv";

const CLASS_LABEL = {
  well_calibrated:         { label: "Well Calibrated",         tone: "pnl-up border-current" },
  mildly_overconfident:    { label: "Mildly Overconfident",    tone: "text-signal-yellow border-current" },
  strongly_overconfident:  { label: "Strongly Overconfident",  tone: "pnl-down border-current" },
  mildly_underconfident:   { label: "Mildly Underconfident",   tone: "text-signal-yellow border-current" },
  strongly_underconfident: { label: "Strongly Underconfident", tone: "pnl-down border-current" },
  insufficient_data:       { label: "Insufficient Data",       tone: "text-muted-foreground border-border/50" },
};

const OUTCOME_RULES = [
  { key: "net_positive_return", label: "Positive net return (default)" },
  { key: "target_reached", label: "Target reached before stop" },
  { key: "alpha_positive", label: "Positive benchmark-relative return" },
];

export default function CalibrationSection({ filterParams }) {
  const [rule, setRule] = useState("net_positive_return");
  const params = { ...filterParams, outcome_rule: rule };
  const { data, isLoading } = useQuery({
    queryKey: qk(["performance", "calibration"], { filters: params }),
    queryFn: async () => (await api.get("/performance/calibration", { params: Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null)) })).data,
    staleTime: STALE.SHORT, gcTime: GC.SHORT,
  });
  if (isLoading || !data) return <div className="grid-cell text-xs text-muted-foreground" data-testid="calibration-loading">Loading calibration…</div>;
  const cls = CLASS_LABEL[data.overall.classification] || CLASS_LABEL.insufficient_data;
  const reliability = (data.reliability_curve || []).filter((r) => r.predicted != null && r.actual != null);

  const exportCsv = () => {
    const rows = data.buckets.map((b) => ({
      range: b.range, n: b.completed_outcome_count, wins: b.win_count, losses: b.loss_count,
      predicted_pct: b.predicted_avg_confidence, actual_pct: b.actual_success_rate_pct,
      gap_pct: b.calibration_gap_pct, brier: b.brier_contribution, data_quality: b.data_quality,
    }));
    downloadCsv("calibration.csv", rows, { context: { outcome_rule: data.outcome_rule, classification: data.overall.classification } });
  };

  return (
    <section className="space-y-3" data-testid="perf-calibration-section" aria-labelledby="perf-cal-title">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 id="perf-cal-title" className="label-cap">Confidence Calibration</h2>
        <span className={`chip !text-[10px] uppercase ${cls.tone}`} data-testid={`calibration-class-${data.overall.classification}`}>{cls.label}</span>
        <span className="text-[10px] mono uppercase text-muted-foreground">Brier {data.overall.brier_score ?? "—"} · ECE {data.overall.expected_calibration_error_pct ?? "—"}pt · n={data.overall.sample_size}</span>
        <select value={rule} onChange={(e) => setRule(e.target.value)}
          className="ml-auto bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
          data-testid="calibration-outcome-rule" aria-label="Outcome rule">
          {OUTCOME_RULES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <button onClick={exportCsv} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="calibration-export">
          <Download size={10} className="inline mr-1"/>CSV
        </button>
      </div>

      {data.overall.sample_size < data.min_classification_sample && (
        <div className="grid-cell border-signal-yellow/40 bg-signal-yellow/5 text-xs flex items-start gap-2" role="status" data-testid="calibration-insufficient">
          <AlertTriangle size={14} className="text-signal-yellow shrink-0 mt-0.5"/>
          <div>Need at least {data.min_classification_sample} completed outcomes to classify calibration confidently. Have {data.overall.sample_size}. Bucket rows still displayed for transparency.</div>
        </div>
      )}

      <div className="grid-cell !p-0 overflow-auto" data-testid="calibration-buckets">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20 border-b border-border/40">
            <tr>
              <th className="text-left px-3 py-2 font-normal">Range</th>
              <th className="text-right px-3 py-2 font-normal">n</th>
              <th className="text-right px-3 py-2 font-normal">Wins</th>
              <th className="text-right px-3 py-2 font-normal">Losses</th>
              <th className="text-right px-3 py-2 font-normal">Predicted</th>
              <th className="text-right px-3 py-2 font-normal">Actual</th>
              <th className="text-right px-3 py-2 font-normal">Gap</th>
              <th className="text-right px-3 py-2 font-normal">Brier</th>
              <th className="text-left px-3 py-2 font-normal">Quality</th>
            </tr>
          </thead>
          <tbody>
            {data.buckets.map((b) => (
              <tr key={b.range} className="border-b border-border/30" data-testid={`cal-bucket-${b.range}`}>
                <td className="px-3 py-2 mono">{b.range}</td>
                <td className="px-3 py-2 mono text-right">{b.completed_outcome_count}</td>
                <td className="px-3 py-2 mono text-right pnl-up">{b.win_count}</td>
                <td className="px-3 py-2 mono text-right pnl-down">{b.loss_count}</td>
                <td className="px-3 py-2 mono text-right">{b.predicted_avg_confidence ?? "—"}</td>
                <td className="px-3 py-2 mono text-right">{b.actual_success_rate_pct ?? "—"}</td>
                <td className={`px-3 py-2 mono text-right ${b.calibration_gap_pct == null ? "" : b.calibration_gap_pct > 5 ? "pnl-down" : b.calibration_gap_pct < -5 ? "text-signal-yellow" : "pnl-up"}`}>{b.calibration_gap_pct ?? "—"}</td>
                <td className="px-3 py-2 mono text-right text-muted-foreground">{b.brier_contribution ?? "—"}</td>
                <td className="px-3 py-2 mono text-[10px] uppercase text-muted-foreground">{b.data_quality}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reliability.length >= 2 && (
        <div className="grid-cell !p-3">
          <div className="label-cap mb-2">Reliability Curve — dashed line is perfect calibration</div>
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <LineChart margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="rgba(148,163,184,0.15)"/>
              <XAxis type="number" dataKey="predicted" domain={[50, 100]} stroke="#64748b" fontSize={10} tickFormatter={(v) => `${v}%`} label={{ value: "Predicted", position: "insideBottom", offset: -4, fontSize: 10 }}/>
              <YAxis type="number" dataKey="actual" domain={[0, 100]} stroke="#64748b" fontSize={10} tickFormatter={(v) => `${v}%`} label={{ value: "Actual", angle: -90, position: "insideLeft", fontSize: 10 }}/>
              <Tooltip formatter={(v) => `${v}%`} labelFormatter={() => ""}/>
              <ReferenceLine segment={[{x:50,y:50},{x:100,y:100}]} stroke="rgba(148,163,184,0.4)" strokeDasharray="4 3"/>
              <Line data={reliability} type="linear" dataKey="actual" name="Model" stroke="#0A84FF" strokeWidth={2} dot={{ r: 4, fill: "#0A84FF" }} isAnimationActive={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
